import { AuthChangeEvent, Session } from '@supabase/auth-js';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
if (!supabaseUrl) {
  throw new Error('REACT_APP_SUPABASE_URL environment variable is required');
}

// Fetch the anon key from the serverless proxy
async function getSupabaseAnonKey() {
  // const res = await fetch('/api/mySecretProxy');
  // if (!res.ok) throw new Error('Failed to fetch Supabase anon key');
  // const json = await res.json();
  // // Expect the proxy to return { result: { anonKey: ... } }
  // if (!json.result || !json.result.anonKey) throw new Error('Invalid anon key response');
  // return json.result.anonKey;

  const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  if (!anonKey) {
    throw new Error('REACT_APP_SUPABASE_ANON_KEY environment variable is required');
  }
  return anonKey;
}

// Export a promise that resolves to the Supabase client
export const supabasePromise = getSupabaseAnonKey().then((anonKey) => {
  if (typeof anonKey !== 'string') {
    throw new Error('Supabase anon key must be a string');
  }
  return createClient(supabaseUrl, anonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  });
});

// Helper to use the client in async functions
export async function getSupabase() {
  return supabasePromise;
}

let publicClient: ReturnType<typeof createClient> | null = null;

export async function getPublicSupabase() {
  if (publicClient) return publicClient;
  if (!supabaseUrl) throw new Error('REACT_APP_SUPABASE_URL is required');
  const anonKey = await getSupabaseAnonKey();
  publicClient = createClient(supabaseUrl, anonKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
    },
  });
  return publicClient;
}

// OAuth provider types
export type OAuthProvider = 'google' | 'github' | 'discord' | 'twitter' | 'linkedin' | 'facebook' | 'apple';

// OAuth sign in function
export const signInWithOAuth = async (provider: OAuthProvider) => {
  const supabase = await getSupabase();
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
      // Apple-specific scopes
      scopes: provider === 'apple' ? 'name email' : undefined,
    },
  });

  if (error) {
    throw error;
  }

  return data;
};

export const signOut = async () => {
  invalidateUserCache();
  const supabase = await getSupabase();
  const { error } = await supabase.auth.signOut();
  if (error) {
    throw error;
  }
};

const USER_CACHE_TTL = 5000;
let userCache: {
  user: Awaited<ReturnType<Awaited<ReturnType<typeof getSupabase>>['auth']['getUser']>>['data']['user'];
  ts: number;
} | null = null;

export const getCurrentUser = async () => {
  const now = Date.now();
  if (userCache && now - userCache.ts < USER_CACHE_TTL) return userCache.user;
  const supabase = await getSupabase();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  userCache = { user, ts: now };
  return user;
};

export const invalidateUserCache = () => {
  userCache = null;
};

const ensureSessionStart = (session: Session | null) => {
  if (!session?.user) return;
  if (typeof window === 'undefined') return;
  if (!localStorage.getItem('session_start')) {
    localStorage.setItem('session_start', Date.now().toString());
  }
};

export const onAuthStateChange = (callback: (event: AuthChangeEvent, session: Session | null) => void) => {
  let unsubscribed = false;
  let subscription: { unsubscribe: () => void } | null = null;

  getSupabase().then((supabase) => {
    if (unsubscribed) return;
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') invalidateUserCache();
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') {
        ensureSessionStart(session);
      }
      callback(event, session);
    });
    subscription = data.subscription;
    if (unsubscribed) {
      subscription.unsubscribe();
      subscription = null;
    }
  });

  return () => {
    unsubscribed = true;
    if (subscription) {
      subscription.unsubscribe();
      subscription = null;
    }
  };
};

// Email/Password sign up function
export const signUpWithEmail = async (email: string, password: string) => {
  const supabase = await getSupabase();
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  });

  if (error) {
    throw error;
  }

  return data;
};

// Email/Password sign in function
export const signInWithEmail = async (email: string, password: string) => {
  const supabase = await getSupabase();
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    throw error;
  }

  return data;
};

// Request password reset function
export const resetPassword = async (email: string) => {
  const supabase = await getSupabase();
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });

  if (error) {
    throw error;
  }

  return data;
};

// Update password function (for reset password flow)
export const updatePassword = async (newPassword: string) => {
  const supabase = await getSupabase();
  const { data, error } = await supabase.auth.updateUser({
    password: newPassword,
  });

  if (error) {
    throw error;
  }

  return data;
};
